import {
	EmailListItemModel,
	EmailSortDirection,
	EmailSortOrder
} from "client/Api/Email/email";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";

class EmailOrdering {

	// find position to insert new email according to currently set sort order in an array of emails
	public static findIndexToInsertInOrder(
		order: EmailSortOrder,
		direction: EmailSortDirection,
		all: readonly EmailListItemModel[],
		a: EmailListItemModel
	): number {
		if (all.length === 0) {
			return 0;
		}

		// binary search for a new place
		// assume for all i: a[i] <= a[i+1]

		// goal: if item <= a[0], return 0
		// if item > a[length-1], return length
		// otherwise return i so that a[i-1] < item <= a[i]

		const length: number = all.length;
		if (EmailOrdering.compareOrder(order, direction, a, all[0]) <= 0) {
			return 0;
		}

		if (EmailOrdering.compareOrder(order, direction, a, all[length - 1]) > 0) {
			return length;
		}
		// now a[0] < item <= a[length - 1]
		// which also implies length > 1

		let low: number = 0;
		let high: number = length - 1;

		// loop invariants:
		// low + 1 <= high
		// a[low] < item <= a[high]
		while (low + 1 < high) {
			const midpoint: number = Math.floor((low + high + 1) / 2);
			// midpoint < floor((high - 1 + high + 1) / 2) = high
			// midpoint > floor((low + low + 1 + 1)/2) = low + 1

			if (EmailOrdering.compareOrder(order, direction, all[midpoint], a) < 0) {
				low = midpoint;
			} else {
				high = midpoint;
			}

			// inequalities above imply that both invariants hold,
			// and loop is finite as range is decreased by at least one each iteration
		}

		// now high = low + 1, so a[low] < item <= a[low+1], so i = high
		return high;
	}


	// compare which email should go first according to the currently selected sort order and direction
	// -1 means left should go before right
	private static compareOrder(
		order: EmailSortOrder,
		direction: EmailSortDirection,
		left: EmailListItemModel,
		right: EmailListItemModel
	): number {
		let baseCompare: (left: EmailListItemModel, right: EmailListItemModel) => number;
		let compare: (left: EmailListItemModel, right: EmailListItemModel) => number;

		switch (order) {
			case EmailSortOrder.From:
				// note we compare by display name, not the actual from email address
				// that's more logical to the end user as he sees ownerName, not from
				baseCompare = (l, r) => EmailOrdering.compareText(l.ownerName, r.ownerName);
				break;
			case EmailSortOrder.Subject:
				baseCompare = (l, r) => EmailOrdering.compareText(l.subject, r.subject);
				break;
			case EmailSortOrder.Date:
			default:
				baseCompare = (l, r) => EmailOrdering.compareDates(
					parseDtoDate(l.receivedDateTime),
					parseDtoDate(r.receivedDateTime)
				);
				break;
		}

		switch (direction) {
			case EmailSortDirection.Ascending:
				compare = baseCompare;
				break;

			case EmailSortDirection.Descending:
			default:
				compare = (l, r) => -baseCompare(l, r);
				break;
		}

		let result: number = compare(left, right);

		// in the group of emails having the same value of the primary sort field, more recent emails go first
		// unless we are already sorting by date
		if ((result === 0) && (order !== EmailSortOrder.Date)) {
			result = -EmailOrdering.compareDates(
				parseDtoDate(left.receivedDateTime),
				parseDtoDate(right.receivedDateTime)
			);
		}

		return result;
	}

	// default compare order to string: a first, z last
	// -1 means left should go before right
	private static compareText(left: string | nil, right: string | nil): number {
		// nulls or undefineds go first
		if ((typeof left === "undefined") || (left === null)) {
			return -1;
		}
		if ((typeof right === "undefined") || (right === null)) {
			return +1;
		}

		// perform case-insensitive comparison
		const leftUpper: string = left.toUpperCase();
		const rightUpper: string = right.toUpperCase();

		if (leftUpper < rightUpper) {
			return -1;
		} else if (leftUpper > rightUpper) {
			return +1;
		} else {
			return 0;
		}
	}

	// default compare order for dates: early to late
	// -1 means left should go before right
	private static compareDates(left: Date | null, right: Date | null): number {
		// nulls go first
		if (left === null) {
			return -1;
		}
		if (right === null) {
			return +1;
		}

		const leftMs: number = left.getTime();
		const rightMs: number = right.getTime();

		return Math.sign(leftMs - rightMs);
	}
}

export {
	EmailOrdering
};
